import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const Login = ({ onLogin }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    department: 'Administration',
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        const response = await axios.post(`${API_BASE}/auth/login`, {
          email: formData.email,
          password: formData.password,
        });
        toast.success('Login successful!');
        onLogin(response.data.token, response.data.user);
      } else {
        await axios.post(`${API_BASE}/auth/register`, formData);
        toast.success('Registration successful! Please login.');
        setIsLogin(true);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen" data-testid="login-page">
      {/* Left Side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-[#FBF8EF]">
        <div className="w-full max-w-md">
          <div className="mb-8 flex flex-col items-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_timesheet-ops-1/artifacts/uwk69pjg_Black-8.png" 
              alt="Abule Imole" 
              className="h-32 w-auto mb-2"
            />
            <p className="text-sm text-[#505631] font-medium">Administration Portal</p>
          </div>

          <div className="bg-white rounded-xl border border-[#E5E2D9] p-8 shadow-sm">
            <h2 className="text-xl sm:text-2xl font-medium tracking-tight text-[#2B3210] mb-6">
              {isLogin ? 'Welcome back' : 'Create account'}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-4">
              {!isLogin && (
                <div>
                  <label className="block text-sm font-medium text-[#2B3210] mb-1.5">
                    Full Name
                  </label>
                  <input
                    type="text"
                    data-testid="register-fullname-input"
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B2560D] focus:border-transparent"
                    required={!isLogin}
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-[#2B3210] mb-1.5">
                  Email
                </label>
                <input
                  type="email"
                  data-testid="login-email-input"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B2560D] focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#2B3210] mb-1.5">
                  Password
                </label>
                <input
                  type="password"
                  data-testid="login-password-input"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B2560D] focus:border-transparent"
                  required
                />
              </div>

              {!isLogin && (
                <div>
                  <label className="block text-sm font-medium text-[#2B3210] mb-1.5">
                    Department
                  </label>
                  <select
                    data-testid="register-department-select"
                    value={formData.department}
                    onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                    className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B2560D] focus:border-transparent"
                  >
                    <option value="Administration">Administration</option>
                    <option value="Hospitality">Hospitality</option>
                    <option value="Facilities Management">Facilities Management</option>
                    <option value="Business Development">Business Development</option>
                    <option value="Environment & Community">Environment & Community</option>
                    <option value="Spirituality">Spirituality</option>
                  </select>
                </div>
              )}

              <button
                type="submit"
                data-testid="login-submit-button"
                disabled={loading}
                className="w-full bg-[#B2560D] text-white py-2.5 rounded-lg hover:bg-[#8A4209] transition-colors duration-200 font-medium disabled:opacity-50"
              >
                {loading ? 'Please wait...' : isLogin ? 'Sign In' : 'Create Account'}
              </button>
            </form>

            <div className="mt-6 text-center space-y-2">
              <button
                data-testid="toggle-auth-mode-button"
                onClick={() => setIsLogin(!isLogin)}
                className="text-sm text-[#B2560D] hover:underline"
              >
                {isLogin ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
              </button>
              {isLogin && (
                <div>
                  <a
                    href="/forgot-password"
                    data-testid="forgot-password-link"
                    className="text-sm text-[#505631] hover:text-[#B2560D] hover:underline"
                  >
                    Forgot your password?
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Image with color overlay */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1644035656974-9f979761e6a1?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1200)',
          }}
        />
        <div 
          className="absolute inset-0"
          style={{
            backgroundColor: '#B2560D',
            mixBlendMode: 'multiply',
            opacity: 0.4,
          }}
        />
      </div>
    </div>
  );
};

export default Login;
